import { PassOrderResult, CHURN_RATE_BY_ORDER } from './passOrder';
import {
    SessionlessInput,
    isSessionless,
    SESSIONLESS_TOTAL_SESSION_MIN,
} from './sessionless';

/**
 * 회원 한 명의 재무 가치 계산.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 이 파일이 따로 있나
 * ─────────────────────────────────────────────────────────────
 * 화면 곳곳에 "80만 원 방어" 같은 금액이 뜹니다. 이게 이 기능의 설득력 전부인데,
 * 예전에는 산출식이 문자열로 적혀 있고 회당 단가가 임의값(5만 원)이었습니다.
 * 근거 없는 금액은 한 번 들키면 화면 전체를 못 믿게 만듭니다.
 *
 * 그래서 실제 결제 정보로만 계산합니다.
 *   - 회당 단가   membership.pricePerSchedule
 *   - 결제 총액   membership.totalPrice
 *   - 재등록 확률 6차 검증 순번별 이탈률의 여집합
 *
 * ─────────────────────────────────────────────────────────────
 * 모르면 만들어내지 않습니다
 * ─────────────────────────────────────────────────────────────
 * 단가가 없는 수강권(무료권·서비스 세션·이관 건)은 null 을 돌려줍니다.
 * 화면은 "금액 산정 불가"로 표시합니다. 0원으로 적으면 "가치 없는 회원"이 되고,
 * 임의값을 넣으면 거짓말이 됩니다.
 */

/**
 * 순번별 재등록 확률(%).
 * CHURN_RATE_BY_ORDER 의 여집합입니다. 숫자를 여기 적지 않습니다 —
 * 실측이 갱신되면 상수 한 곳만 바뀌고 이 파생은 따라옵니다.
 */
export const REREGISTRATION_RATE_BY_ORDER: Record<number, number> = Object.fromEntries(
    Object.entries(CHURN_RATE_BY_ORDER).map(([order, churn]) => [
        Number(order),
        Math.round((100 - churn) * 10) / 10,
    ])
);

/**
 * 첫 수강권 → 두 번째로 넘어가는 비율(%). 회원 현황의 「라포 평균」이 이것입니다.
 *
 * ⚠️ 화면에 숫자를 직접 적지 마세요. 이 값을 읽어서 렌더하세요.
 * 예전에는 "라포 평균 31.7%" 가 화면에 박혀 있었습니다. 그 31.7 은 폐기된
 * 이탈률 68.3 의 여집합이라, 실측이 갱신된 뒤에도 옛 숫자를 계속 말했습니다.
 * (`passOrder.ts:194` — 68.3 / 48.2 / 34.4 는 레포 어디에서도 재현되지 않아 폐기)
 * 원천은 `CHURN_RATE_BY_ORDER` 하나이고, 이 값은 거기서 유도됩니다.
 */
export const FIRST_PASS_REREGISTRATION_RATE = REREGISTRATION_RATE_BY_ORDER[1];

/**
 * 재등록률을 모를 때 쓰는 값 — 3번째 값(63.5%)입니다.
 *
 * ⚠️ **「보수적」인지는 왜 몰랐느냐에 따라 정반대입니다.** 두 경우를 가르세요.
 *
 *   · **4번째 이상** — 검증된 값이 없지만 순번이 올라갈수록 이탈률이 떨어지는
 *     방향은 확인됐습니다. 그래서 63.5% 는 **하한선**이고, 쓰면 과소평가 쪽입니다.
 *     이게 이 상수를 만든 원래 이유입니다.
 *
 *   · **순번 자체를 모를 때**(basis 'NONE') — 실제 순번은 1·2번째일 가능성이
 *     높은데, 63.5% 는 알려진 셋 중 **가장 높은 값**입니다(52.9 / 53.4 / 63.5).
 *     이때는 보수적이기는커녕 금액을 **부풀립니다.**
 *
 * 실제로 2026-08-31 감사에서 그 사고가 잡혔습니다 — 오늘의 브리핑이 순번 원본을
 * 안 보고 전원에게 이 값을 써서, 헤더 합계가 회원 현황보다 8.2% 높았습니다
 * (`briefingValue.valueOfItem` 주석). 그러니 이 값으로 떨어진 금액을 보면
 * **「위로 치우쳤을 수 있다」** 고 읽어야지 「보수적이다」로 읽으면 안 됩니다.
 */
export const FALLBACK_REREGISTRATION_RATE = REREGISTRATION_RATE_BY_ORDER[3];

export type ValueInput = SessionlessInput & {
    /** 잔여 회차. 회차 정보가 온전하지 않으면 null */
    remain: number | null;
    /** 회당 단가(원). 없으면 null */
    pricePerSchedule: number | null;
    /** 현재 수강권 결제 총액(원). 다음 수강권 예상액의 기준 */
    totalPrice: number | null;
    totalSession: number;
};

/**
 * 한 수강권에 걸린 돈 — **성격이 다른 두 숫자를 섞지 않습니다.**
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 2026-08-31 대표 확정본(OPS_BRIEFING_MONEY_POLICY v2) — 왜 갈랐나
 * ─────────────────────────────────────────────────────────────
 * 예전에는 `kind` 하나로 한 숫자만 냈습니다.
 *   DEFEND → 잔여분 + 재등록 기대   ·   GAIN → 재등록 기대만
 * 그리고 그 `kind` 를 회원 **단계**로 갈랐습니다(`adapters.valueKindOf`).
 * 결과가 둘이었습니다:
 *
 *   ① **골든타임·안정 궤도 회원의 선수금이 어느 합계에도 안 잡혔습니다.**
 *      가장 많은 선수금을 들고 있는 집단인데 GAIN 이라 잔여분이 통째로 빠졌습니다.
 *      그래서 단계별 네 숫자의 **뜻이 서로 달랐습니다** — ①④는 「잔여+기대」,
 *      ②③은 「기대만」. 같은 줄에 나란히 놓고 더할 수 없는 숫자들이었습니다.
 *   ② **이탈 위험 회원에게 재등록률을 곱했습니다.** 한 카드가 "이 회원 떠납니다"와
 *      "재등록 52.9% 기대합니다"를 동시에 말했습니다. 화면이 스스로 모순됩니다.
 *
 * 그래서 이제 계산기는 **두 숫자를 따로** 냅니다. 어느 쪽을 어떻게 합칠지는
 * 화면이 정하고, 화면은 둘을 절대 한 칸에 더하지 않습니다.
 *
 * ⚠️ `amount` 필드를 되살리지 마세요. 그 한 필드가 위 두 사고의 원인이었습니다 —
 *    「이 회원에게 걸린 돈」이라는 이름 아래 성격이 다른 돈이 섞였고, 어느 화면이
 *    무엇을 세고 있는지 아무도 몰랐습니다.
 */
export type MemberValue = {
    /**
     * 잔여 가치 = **미사용 선수금** (정책 M1).
     *   잔여 회차 × 회당 단가.
     *
     * 이미 받은 돈이고 수업 의무가 남아 있습니다. 회계로는 부채(이연수익)입니다.
     * 못 세면 `null` 입니다 — 단가를 모르거나(0 포함) 잔여 회차를 모르는 경우.
     * **0 은 「모른다」가 아니라 「다 썼다」입니다.** 둘을 같게 두면 소진된 회원이
     * 「금액 산정 불가」로 잡혀, 고칠 데이터가 없는데 고치라고 화면이 말합니다.
     */
    prepaid: number | null;
    /**
     * 재등록 기대 (정책 M3).
     *   다음 수강권 예상액 × 순번별 재등록률.
     *
     * 아직 안 들어온 돈이고 확률을 곱한 기댓값입니다. 확정 매출이 아닙니다.
     * 못 세면 `null`(다음 수강권 예상액의 근거가 없음),
     * **기대하지 않기로 한 자리면 `0`** 입니다 (이탈 위험·주의 — 정책 M-D2).
     */
    expected: number | null;
    /** 화면에 그대로 보여줄 산출식 */
    breakdown: string;
    /**
     * 선수금의 **근거 한 조각** — 「잔여 15회 × 55,000원」.
     *
     * ─────────────────────────────────────────────────────────────
     * 🔴 왜 `breakdown` 으로는 안 되나 (2026-09-01 QA31)
     * ─────────────────────────────────────────────────────────────
     * 「이 금액이 왜 이 금액인가」 팝업은 회원 수백 명을 **한 줄씩** 늘어놓습니다.
     * `breakdown` 은 ※ 안내까지 붙은 여러 줄짜리라 목록 한 칸에 못 들어갑니다.
     *
     * 그렇다고 팝업에서 `잔여 × 단가` 를 다시 조립하면 **같은 식이 두 곳**에
     * 생깁니다. 그러면 계산기가 `Math.max(remain, 0)` 같은 예외를 하나 더
     * 두는 순간 화면과 합계가 조용히 갈립니다. 그래서 문자열도 **계산기가**
     * 만들고, 화면은 읽기만 합니다 — 아래 `breakdown` 도 이 값을 씁니다.
     *
     * 못 셌으면 `null` 입니다.
     */
    prepaidBasis: string | null;
    /**
     * 재등록 기대의 근거 한 조각 — 「다음 수강권 예상 220만 원 × 1번째 재등록률 52.9%」.
     *
     * `null` 인 경우가 둘이고 **뜻이 다릅니다.**
     *   · 근거 자체가 없다 (`expected == null`) — 모른다
     *   · 기대하지 않기로 한 자리다 (이탈 위험·주의, `expected === 0`) — 곱할 식이 없다
     * 화면은 `expected` 값으로 둘을 가릅니다.
     */
    expectedBasis: string | null;
    /** 재등록 확률을 검증된 값으로 썼는가 */
    rateVerified: boolean;
};

/**
 * 이 자리에서 재등록을 기대해도 되는가.
 *
 * 정책 M-D2 — **이탈 위험·주의 회원의 재등록 기대는 0** 입니다.
 * 「이 회원 떠납니다」라고 카드를 띄우면서 같은 카드에서 재등록률을 곱하면
 * 한 화면이 스스로 모순됩니다. 실측(M-Q6: 위험 판정 이력 × 이후 재등록 여부)이
 * 쌓이면 그 실측 요율로 올릴 자리이고, 그때까지는 0 입니다.
 *
 * ⚠️ 「기대 0」과 「기대를 못 셈(null)」은 다릅니다. 여기서 0 으로 눕히는 것은
 *    **셀 수 있었는데 정책으로 안 세는 것**입니다. 근거 자체가 없으면 null 입니다.
 */
export type ValueOptions = {
    /** false 면 재등록 기대를 0 으로 눕힌다 (이탈 위험·주의) */
    expectsReRegistration: boolean;
};

/**
 * 금액 정보가 실제로 있는가.
 *
 * ─────────────────────────────────────────────────────────────
 * 0원은 "0원짜리 수강권"이 아니라 "금액을 모르는 수강권"입니다
 * ─────────────────────────────────────────────────────────────
 * 라포 도입 전 데이터를 옮겨 온 수강권(issuanceType: MIGRATED)은
 * `pricePerSchedule: 0, totalPrice: 0` 으로 옵니다. 결제 정보가 넘어오지 않았을 뿐,
 * 회원이 공짜로 다니는 게 아닙니다.
 *
 * 이걸 그대로 계산에 넣으면 "잔여 1회 × 0원 = 0원" 이 나오고, 화면에는
 * **이탈 리스크 0원**이라고 찍힙니다. 원장님은 그 회원을 안 챙겨도 되는 사람으로 읽습니다.
 * null 과 똑같이 다뤄서 화면이 「금액 산정 불가」라고 말하게 합니다 —
 * 단계 카드의 "+N명 산정 불가"와 같은 규칙입니다.
 *
 * 음수도 같이 걸러냅니다. 서버 계산 실수로 음수가 와도 금액을 깎으면 안 됩니다.
 */
const priced = (n: number | null | undefined): number | null => (n != null && n > 0 ? n : null);

const won = (n: number) => n.toLocaleString('ko-KR');
/** 만 원 단위로 읽기 좋게 */
const manwon = (n: number) => `${Math.round(n / 10000).toLocaleString('ko-KR')}만 원`;

/** 다음 수강권 예상 결제액 — 보수적으로 현재와 같은 금액으로 본다 */
export const expectedNextPayment = (input: ValueInput): number | null => {
    /*
     * 회차 개념이 없는 수강권(무제한·대관)은 여기서 멈춥니다.
     *
     * 되짚기 식(`단가 × 총 회차`)에 999 가 들어가면 한 건이 수천만 원이 됩니다.
     * 결제 총액이 있는 경우도 쓰지 않습니다 — 「다음 수강권 예상액」은 순번별
     * **재등록률**과 곱해지는 값인데, 그 재등록률은 회차 수강권 코호트에서
     * 실측한 값(passOrder.CHURN_RATE_BY_ORDER)입니다. 대관을 그 확률에 태우면
     * 근거 없는 기댓값이 만들어집니다. 모르면 모른다고 합니다.
     */
    if (isSessionless(input)) return null;

    const total = priced(input.totalPrice);
    if (total != null) return total;
    // 총액이 없으면 단가 × 총 회차로 되짚는다
    const unit = priced(input.pricePerSchedule);
    if (unit != null && input.totalSession > 0) return unit * input.totalSession;
    return null;
};

const rateOf = (order: PassOrderResult): { rate: number; verified: boolean } => {
    const known = REREGISTRATION_RATE_BY_ORDER[order.order];
    if (known != null && !order.isAtLeast) return { rate: known, verified: true };
    /*
     * 순번이 하한선이거나(AT_LEAST) 검증 구간 밖이거나 아예 모를 때(NONE).
     * ⚠️ 「보수적」이 아니다 — 방향은 왜 몰랐느냐에 달렸다
     *    (FALLBACK_REREGISTRATION_RATE 주석).
     */
    return { rate: known ?? FALLBACK_REREGISTRATION_RATE, verified: false };
};

/**
 * 이 수강권에 걸린 돈 — **잔여 가치와 재등록 기대를 따로** 낸다.
 *
 * 둘 다 셀 수 없으면 `null` 입니다 = 「금액 산정 불가」.
 * 한쪽만 셀 수 있으면 그쪽만 채우고 다른 쪽은 `null` 로 둡니다 —
 * **모르는 칸을 0 으로 메우지 않습니다.** 0 은 「없다」이지 「모른다」가 아닙니다.
 */
export const computeMemberValue = (
    input: ValueInput,
    order: PassOrderResult,
    opts: ValueOptions
): MemberValue | null => {
    /*
     * 회차 개념이 없는 수강권(무제한·대관)은 금액을 만들지 않습니다.
     *
     * 잔여분(`잔여 × 단가`)은 999 가 들어가 3천만 원짜리 허수가 되고,
     * 다음 수강권 기대액은 회차 수강권 코호트의 재등록률을 쓰는 값이라
     * 대관에 곱할 근거가 없습니다 (expectedNextPayment 주석 참고).
     *
     * null 을 돌려주면 화면은 이미 있는 규칙 그대로 「금액 산정 불가」로 적고,
     * 단계 카드 합계·지켜야 할 매출·확보 가능한 매출에서 빠지며
     * 「+N명 산정 불가」에 합류합니다 — 새 표시 규칙을 만들지 않습니다.
     */
    if (isSessionless(input)) return null;

    const next = expectedNextPayment(input);
    const { rate, verified } = rateOf(order);

    /*
     * 잔여 가치(M1) — 0원 단가는 '공짜'가 아니라 '모른다'로 본다 (priced 주석).
     * 잔여 회차를 모르면(null) 셀 수 없다. **0 으로 눕히지 않는다** —
     * 0 으로 두면 「다 쓴 회원」이 되어 재등록 상담 대상으로 잘못 읽힌다.
     *
     * 잔여가 음수로 오면 0 으로 막는다. 소진(0)은 「선수금 0원」이고,
     * 이건 정책 M10 이 말하는 「소진은 선수금에서 제외」와 같은 결과다.
     */
    const unitPrice = priced(input.pricePerSchedule);
    const prepaid =
        unitPrice == null || input.remain == null ? null : Math.max(input.remain, 0) * unitPrice;

    /*
     * 재등록 기대(M3).
     *
     * 근거(다음 수강권 예상액)가 없으면 null — 「모른다」입니다.
     * 근거는 있는데 기대하지 않기로 한 자리(이탈 위험·주의)면 **0** 입니다 —
     * 「셀 수 있었지만 세지 않기로 했다」. 둘을 같게 두면 안 됩니다:
     * null 은 화면이 「산정 불가」에 넣고, 0 은 합계에 0 으로 들어갑니다.
     */
    const expected =
        next == null ? null : opts.expectsReRegistration ? Math.round((next * rate) / 100) : 0;

    /*
     * 둘 다 못 셌으면 금액을 만들지 않습니다.
     *
     * ⚠️ 예전에는 「합이 0 이하면 null」이었습니다. 그 규칙은 **소진된 회원**
     *    (잔여 0회 · 단가는 아는)까지 「금액 산정 불가」로 밀어 넣었습니다.
     *    선수금 0원은 아는 사실이지 모르는 사실이 아닙니다.
     *    (2026-08-31 실서버 279건에서 이 경우는 0건이라 숫자는 안 움직입니다)
     */
    if (prepaid == null && expected == null) return null;

    /*
     * 근거 문자열은 **여기서 한 번만** 만듭니다. 아래 `breakdown` 도, 화면의
     * 「왜 이 금액인가」 팝업도 같은 값을 읽습니다 — 두 곳에서 조립하면
     * 계산 규칙이 바뀔 때 한쪽만 낡습니다 (`prepaidBasis` 주석).
     */
    const prepaidBasis =
        prepaid == null ? null : `잔여 ${input.remain}회 × ${won(unitPrice as number)}원`;
    /*
     * ⚠️ 순번을 모르면 「0번째 재등록률」이 된다 — 없는 순번이다.
     *    툴팁에만 있을 때는 눈에 안 띄었지만, 이제 산출 근거 팝업의 한 줄로
     *    크게 뜬다. 모르면 순번을 말하지 않는다 (왜 그 요율인지는 아래
     *    「※ 순번을 확정할 수 없어…」 줄이 이어서 밝힌다).
     */
    const rateLabel = order.order > 0 ? `${order.order}번째 재등록률` : '재등록률';
    const expectedBasis =
        expected == null || !opts.expectsReRegistration
            ? null
            : `다음 수강권 예상 ${manwon(next as number)} × ${rateLabel} ${rate}%`;

    const lines: string[] = [];
    if (prepaidBasis == null) {
        lines.push(
            unitPrice == null
                ? '※ 회당 단가가 없는 수강권이라 미사용 선수금은 계산에서 뺐습니다'
                : '※ 잔여 회차를 알 수 없어 미사용 선수금은 계산에서 뺐습니다'
        );
    } else {
        lines.push(`미사용 선수금 = ${prepaidBasis} = ${manwon(prepaid as number)}`);
    }

    if (expected == null) {
        lines.push('※ 다음 수강권 예상액의 근거가 없어 재등록 기대는 세지 않았습니다');
    } else if (!opts.expectsReRegistration) {
        /*
         * 0 을 적기만 하면 「돈이 없는 회원」으로 읽힙니다. **왜 0 인지**를 적습니다 —
         * 데이터가 없어서가 아니라 이 단계에서는 재등록을 기대하지 않기로 했기 때문입니다.
         */
        lines.push(
            `재등록 기대 0원 — 이탈 위험·주의 단계에서는 재등록을 기대하지 않습니다 ` +
                `(다음 수강권 예상 ${manwon(next as number)} 은 기대에 넣지 않았습니다)`
        );
    } else {
        lines.push(`재등록 기대 = ${expectedBasis} = ${manwon(expected)}`);
        if (!verified) {
            /*
             * 「보수적으로」라고 적지 않는다. 순번을 모를 때 이 값은 알려진 셋 중
             * 가장 높은 값이라 금액이 위로 치우친다 — 원장이 반대로 읽으면 안 된다.
             */
            lines.push(
                `※ 순번을 확정할 수 없어 재등록률은 3번째 값 ${FALLBACK_REREGISTRATION_RATE}% 를 적용했습니다`
            );
        }
    }

    /* 다음 수강권 예상액은 「직전과 같은 금액」이다 — 정책 M5. 화면이 반드시 밝힌다 */
    if (expected != null && expected > 0) {
        lines.push('※ 다음 수강권 예상액은 직전과 같은 금액으로 보수적으로 잡았습니다');
    }
    if (prepaid != null && prepaid > 0) {
        lines.push('※ 미사용 선수금은 환불 규정에 따라 실제 지급액이 달라질 수 있습니다');
    }

    return {
        prepaid,
        expected,
        breakdown: lines.join('\n'),
        prepaidBasis,
        expectedBasis,
        rateVerified: verified,
    };
};

/** 금액을 못 구했을 때 화면에 쓸 안내 */
export const VALUE_UNAVAILABLE_TEXT = '금액 산정 불가';
export const VALUE_UNAVAILABLE_REASON =
    '이 수강권에는 회당 단가와 결제 금액이 없어(0원 포함) 금액을 계산하지 않았습니다. ' +
    '무료권·서비스 세션이거나, 라포 도입 전 기록을 옮겨 와 결제 정보가 넘어오지 않은 수강권일 수 있습니다.';

/**
 * 무제한 수강권이라 금액을 안 센 경우의 안내.
 *
 * 위의 안내("단가가 없어서")를 그대로 쓰면 **틀린 말**입니다.
 * 대관 수강권에는 단가도 결제액도 있습니다 — 셀 수 없는 것은 회차입니다.
 * 이유가 다르면 원장님이 고칠 방법도 달라지므로 문장을 가릅니다.
 */
export const VALUE_UNAVAILABLE_SESSIONLESS_REASON =
    '회차 개념이 없는 수강권(무제한·대관)이라 잔여 회차로 금액을 셀 수 없습니다. ' +
    `등록상 총 회차가 ${SESSIONLESS_TOTAL_SESSION_MIN}회 이상이면 무제한으로 봅니다(운영 관례). ` +
    '999회 × 회당 단가를 매출로 잡으면 없는 돈이 센터 합계에 들어갑니다.';

/** 이 수강권의 금액이 왜 비어 있는가 — 화면 툴팁은 전부 이 함수를 쓴다 */
export const valueUnavailableReason = (m: SessionlessInput): string =>
    isSessionless(m) ? VALUE_UNAVAILABLE_SESSIONLESS_REASON : VALUE_UNAVAILABLE_REASON;
